// @ts-strict-ignore

import { AppConstants } from '@utils/app.constants';
import { LMActionRequest } from '@services/lm-action.service';
import { LMModelMultiPartFields, LMModelXLMultiPartFields, LMXLFileUploadEvent } from '@form/model/lm-model';
import { Subject } from 'rxjs';
export type LMMultiPartFields = LMModelMultiPartFields;

export class LMTransactionState {

  private _url: string;

  public isFinished: boolean;
  public isCanceled: boolean;
  public isError: boolean;
  public isSSORedirecting: boolean;
  public dataViewIndex = 0;
  public response: any;
  public actionFormDialogSaved: boolean;
  public postRegUrl: string;
  public busFormSpec: LMBusFormSpec;
  public busListSpec: LMBusListSpec;
  public multiPartFields: LMModelMultiPartFields;
  public xlMultiPartFields: LMModelXLMultiPartFields;
  public xlFileUploadSubject$: Subject<LMXLFileUploadEvent>;
  public templateMultiPartFields: LMModelMultiPartFields[];
  public parameterView: LMDataView;
  public isMatrixForm = false;
  public redirectUrlToGuestReg: string;
  public postRegUrlFromGuestAction = false;

  public cancelledRTConfirmations: string[] = [];
  public transactionRequestList: LMActionRequest[];          // original request payload
  public pendingTransactionRequestList: LMActionRequest[];   // requests that need to be sent to server
  public completedTransactionList: LMActionResult[] = [];    // requests that have completed

  constructor(url: string) {
    this._url = url;
  }

  get url(): string {
    return this._url;
  }

  get hasPendingTransactions(): boolean {
    return this.pendingTransactionRequestList.length > 0;
  }

  public setRequestPayload(transactionRequestList: LMActionRequest[]) {
    this.transactionRequestList = transactionRequestList;
    this.pendingTransactionRequestList = [];
    this.dataViewIndex = 0;
  }

  public getPendingDataViews(): LMDataView[] {
    const list: LMDataView[] = [];
    for (let i = 0; i < this.transactionRequestList.length; i++) {
      list.push(this.transactionRequestList[ i ].dataView);
    }
    return list;
  }

  public resetTransactionRequest() {
    this.transactionRequestList = this.pendingTransactionRequestList;
    this.pendingTransactionRequestList = [];
    this.dataViewIndex = 0;
  }

  // LMWEB-2151 Need to reset the pendingTransactionRequestList when clicking the "Ok To All" on Confirmation Required,
  // if trying to run against multiple records and had selected "Ok" on first few.
  public resetPendingTransactionRequest() {
    this.pendingTransactionRequestList = [];
    this.completedTransactionList = [];
    this.dataViewIndex = 0;
  }

  public cancel() {
    if (!this.isFinished) {
      this.isFinished = true;
      this.isCanceled = true;
    }
  }

  public manualRepResetCancel(): void {
    if (this.isCanceled) {
      this.isCanceled = false;
    }
  }

  public finish() {
    if (!this.isFinished) {
      this.isFinished = true;
    }
  }

  public error(error: any) {
    if (!this.isFinished) {
      this.response = error;
      if (error?.status && error.status === 400 && error.error?.details && error.error.details.indexOf('malicious script') >= -1 ) {
        // The error object of a security exception is tucked into the HTTPResponse object itself as error.error. Extracting it out here
        // so all the transaction message handlers do not have to change in form or list or menu.
        this.response = error.error;
      }
      this.isFinished = true;
      this.isError = true;
      this.isSSORedirecting = this.response.status === AppConstants.STATUS_SSO_REDIRECTING;
    }
  }

  public queueRequest(request: LMActionRequest) {
    this.pendingTransactionRequestList.push(request);
  }

  public completeResult(result: LMActionResult) {
    this.completedTransactionList.push(result);
  }

  public addCancelledRTConfirmation(result: LMActionResult) {
    this.cancelledRTConfirmations.push(result.message);
    this.completeResult(result);
  }

  public hasCancelledRTConfirmationMessage(message: string): boolean {
    return this.cancelledRTConfirmations.filter(m => m === message).length > 0;
  }

  public hasCancelledRTConfirmationResults(): boolean {
    return this.cancelledRTConfirmations.length > 0;
  }

  public hasViewExceptionError(): boolean {
    if (this.hasCancelledRTConfirmationResults()) {
      return false;
    }
    for (let i = 0, l = this.completedTransactionList.length; i < l; i++) {
      const request = this.completedTransactionList[ i ];
      if (AppConstants.VIEW_EXCEPTION === request.status)
        return true
      }
    return false;
  }

  public isSuccess(): boolean {
    if (this.isError || this.isCanceled) {
      return false;
    }

    for (let i = 0, l = this.completedTransactionList.length; i < l; i++) {
      const request = this.completedTransactionList[ i ];
      if (AppConstants.STATUS_COMPLETED !== request.status) {
        return false;
      }
    }
    return true;
  }

  public getTransactionResult(): LMActionResult {
    if (this.completedTransactionList.length > 0) {
      return this.completedTransactionList[ 0 ];
    }
    return null;
  }

  public allRequestsFailedForSameReason(): boolean {
    const firstMessage = this.getTransactionResult().message;

    for (let i = 0, l = this.completedTransactionList.length; i < l; i++) {
      const request = this.completedTransactionList[i];

      if (AppConstants.VIEW_EXCEPTION !== request.status) {
        return false;
      }

      if (firstMessage !== request.message) {
        return false;
      }
    }
    return true;
  }

  public allRequestsSuccessfulForSameReason(): boolean {
    const firstMessage = this.getTransactionResult().message;
    for (let i = 0, l = this.completedTransactionList.length; i < l; i++) {
      const request = this.completedTransactionList[i];

      if (AppConstants.STATUS_COMPLETED !== request.status) {
        return false;
      }
      if (firstMessage !== request.message) {
        return false;
      }
    }
    return true;
  }
}
